import { Base64 } from 'js-base64'
import { StorageKeys, useEnvStore, userDeviceId, useUserInfoStore } from 'store'
import { UiUtils } from 'ui-components'
import { createApiSign, random8Lower, toCamelCase, transformApiParams, transformApiResponse } from 'utils'
import { i18n } from '../../i18n-utils/src'
import { PlatformSource } from './constants/platform'
import { ApiStatus, LoginExpiredStatusList } from './constants/status'

export const API_VERSION = '5.1.4'

export const ZIP_TYPE = 1

/** web端 */
export const API_PLATFORM = PlatformSource.Web
interface RequestOptions {
  headers?: HeadersInit
  timeout?: number
}

export interface CustomRequestConfig {
  ignoreToCamelCaseFields?: string[]
  ignoreToCamelCase?: boolean
  isShowError?: boolean
  /** 在签名时忽略这些字段（已是下划线或原始字段名，和 transform 无强相关） */
  ignoreSignFields?: string[]
  /** 是否在签名时自动排除 File/Blob 等二进制字段，默认 true */
  excludeFileFromSign?: boolean
}

export type RequestInterceptor = (
  url: string,
  options: RequestInit,
) => {
  url: string
  options: RequestInit
}
/** 响应拦截器 */
export type ResponseInterceptor = (url: string, options: RequestInit, result: FetchResponse<any>) => any

export class RequestService {
  public timeoutId: NodeJS.Timeout | null = null
  public controllerMap: Map<string, AbortController> = new Map()
  public baseURL: string = ''
  private _requestInterceptor?: RequestInterceptor
  private _responseInterceptor?: ResponseInterceptor
  private errorDialogMap: Map<number, boolean> = new Map()

  constructor(
    public config: RequestOptions = {
      headers: {},
      timeout: 30000,
    },
  ) {}

  private getCommonData() {
    const envStore = useEnvStore()
    const userStore = useUserInfoStore()
    if (!envStore.config || Object.keys(envStore.config).length === 0) return {}
    const appInfo = envStore.config.appInfo
    const tempInfo = appInfo[0].slice(2) + appInfo[1]
    const webAppIdKeyInfo = JSON.parse(Base64.decode(tempInfo))
    const deviceId = userStore.getDeviceId?.() || userDeviceId

    const lang = window.localStorage.getItem(StorageKeys.CurrentLanguage) || 'zh_CN'
    const token = window.localStorage.getItem(StorageKeys.Token) || ''
    return {
      deviceId: deviceId,
      appId: webAppIdKeyInfo.app_id,
      appKey: webAppIdKeyInfo.app_key,
      token,
      __version: API_VERSION,
      __platform: API_PLATFORM,
      __timestamp: Date.now(),
      lang,
    }
  }

  private getFullBody(params: Record<string, any>, ignoreToCamelCaseFields?: string[], signOptions?: { ignoreSignFields?: string[]; excludeFileFromSign?: boolean }) {
    const commonData = this.getCommonData()
    const transformedData = transformApiParams(
      {
        ...commonData,
        ...(params || {}),
      },
      ignoreToCamelCaseFields,
    )
    // 生成并覆盖用于签名与发送的一致时间戳与随机数
    const now = Date.now()
    const timestampSecStr = Math.round(now / 1000) + ''
    const nonce = timestampSecStr.substr(-6)
    ;(transformedData as Record<string, any>)['__timestamp'] = now
    ;(transformedData as Record<string, any>)['nonce'] = nonce

    const excludeFileFromSign = signOptions?.excludeFileFromSign !== false
    const ignoreSignFields = signOptions?.ignoreSignFields || []

    const buildSignBody = (obj: any): any => {
      if (obj === null || obj === undefined) return obj
      if (Array.isArray(obj)) {
        return obj.map((item) => buildSignBody(item)).filter((item) => item !== undefined)
      }
      if (typeof obj === 'object') {
        const result: Record<string, any> = {}
        Object.keys(obj).forEach((key) => {
          if (ignoreSignFields.includes(key)) return
          const value = obj[key]
          if (excludeFileFromSign && (value instanceof File || value instanceof Blob)) {
            return
          }
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            const nested = buildSignBody(value)
            // 保持与原逻辑一致：嵌套对象原样参与（多数后端会忽略复杂值或有自定义处理）
            result[key] = nested
          } else {
            result[key] = value
          }
        })
        return result
      }
      return obj
    }

    const signBody = buildSignBody(transformedData)

    const sign = createApiSign(signBody)
    if (Object.prototype.hasOwnProperty.call(transformedData, 'app_key')) {
      delete (transformedData as Record<string, any>)['app_key']
    }

    return {
      sign,
      data: transformedData,
    }
  }

  private _logout() {
    const userStore = useUserInfoStore()
    userStore.logout()
  }

  private _fetch<R = any, J extends boolean = false>(url: string, options: RequestInit & CustomRequestConfig = {}): Promise<J extends true ? R : FetchResponse<R>> {
    this.updateBaseURL()
    let fullUrl = url.startsWith('http') ? url : this.baseURL + url
    const controllerKey = fullUrl + JSON.stringify(options.body || {})
    this.controllerMap.set(controllerKey, new AbortController())
    const controller = this.controllerMap.get(controllerKey)

    const timeoutId = setTimeout(() => {
      controller?.abort?.({
        msg: '网络超时',
        status: ApiStatus.Timeout,
      })
    }, this.config.timeout)

    const { sign, data } = this.getFullBody(options.body as Record<string, any>, options.ignoreToCamelCaseFields, {
      ignoreSignFields: options.ignoreSignFields,
      excludeFileFromSign: options.excludeFileFromSign,
    })
    const body: Record<string, any> = {
      ...transformApiParams(data, options.ignoreToCamelCaseFields),
      _CDCODE: sign,
    }
    const formData = new FormData()
    Object.keys(body).forEach((key) => {
      formData.append(key, body[key])
    })

    const opt: RequestInit = {
      ...options,
      signal: controller!.signal,
      method: options.method || 'POST',
      headers: {
        ...this.config.headers,
        ...(options.headers || {}),
        'XP-Log-Id': `${body['__timestamp']}${random8Lower()}`,
      },
      body: formData,
    }

    if (fullUrl.includes('articles')) {
      delete (opt.headers as HeadersInit & Record<string, string>)['XP-Log-Id']
    }

    if (options.method === 'GET') {
      delete opt.body
    }

    let requestOpt = opt

    if (this._requestInterceptor) {
      const config = this._requestInterceptor(fullUrl, opt)
      if (config.url) {
        fullUrl = url
      }
      if (config.options) {
        requestOpt = config.options
      }
    }

    return new Promise<J extends true ? R : FetchResponse<R>>((resolve, reject) => {
      fetch(fullUrl, requestOpt)
        .then<J extends true ? R : FetchResponse<R>>((response) => {
          return response.json()
        })
        .then(async (_res) => {
          const tempRes = _res as FetchResponse<R>
          const status = +tempRes.status
          if (fullUrl.includes('articles')) {
            return resolve(transformApiResponse(tempRes) as J extends true ? R : FetchResponse<R>)
          }
          const ignoreErrorStatusList = [ApiStatus.RiskWarning, ApiStatus.Forbidden]
          if (status === ApiStatus.RiskWarning && !this.errorDialogMap.get(status)) {
            this.errorDialogMap.set(status, true)
            UiUtils.showModal(tempRes.msg, {
              title: i18n.global.t('风险提示'),
            })
          }
          if (status === ApiStatus.Forbidden && !this.errorDialogMap.get(status)) {
            this.errorDialogMap.set(status, true)
            return UiUtils.showModal(tempRes.msg, {
              title: i18n.global.t('风险提示'),
              closeOnClickModal: false,
              onConfirm: async () => {
                window.location.href = 'about:blank'
              },
              onClose: () => {
                window.location.href = 'about:blank'
              },
            })
          }

          if (tempRes.status !== ApiStatus.Success) {
            if (tempRes.status)
              if (tempRes.status === ApiStatus.KycVerify1 || tempRes.status === ApiStatus.KycVerify2) {
                // 处理特殊错误码：需要kyc认证
                await UiUtils.showKycDialog()
                resolve(transformApiResponse(tempRes) as J extends true ? R : FetchResponse<R>)
                return
              }

            let msg = tempRes.msg.trim()
            if (LoginExpiredStatusList.includes(tempRes.status) && !msg) {
              msg = '请重新登录'
            }

            if (!ignoreErrorStatusList.includes(status)) {
              options.isShowError && UiUtils.showError(msg)
            }
            if (LoginExpiredStatusList.includes(tempRes.status)) {
              this._logout()
            }
          }

          if (fullUrl.includes('WebLangPackage')) {
            const res = this._responseInterceptor?.(fullUrl, requestOpt, tempRes)
            resolve(res ? res : tempRes)
          } else {
            // 如果J为true，则返回R类型，否则返回FetchResponse<R>类型
            const result = options.ignoreToCamelCase ? tempRes : transformApiResponse(tempRes)
            const res = this._responseInterceptor?.(fullUrl, requestOpt, result as FetchResponse<R>)
            resolve(res ? res : result)
          }
        })
        .catch((error) => {
          if (error.status === ApiStatus.Timeout) {
            UiUtils.showError(i18n.global.t('网络超时'))
            return resolve(error)
          }
          const errorResult = transformApiResponse(error)
          const res = this._responseInterceptor?.(fullUrl, requestOpt, errorResult as FetchResponse<R>)
          reject(res ? res : errorResult)
        })
        .finally(() => {
          clearTimeout(timeoutId)
        })
    })
  }

  public post<R = any>(
    url: string,
    data?: any,
    options: RequestInit & CustomRequestConfig = {
      isShowError: true,
      ignoreToCamelCase: false,
    },
  ) {
    return this._fetch<R>(url, {
      ...options,
      method: 'POST',
      body: data,
    })
  }

  public async postZip<R = any>(url: string, data?: any, options: RequestInit & CustomRequestConfig = {}) {
    const res = await this._fetch<R>(url, {
      ...options,
      method: 'POST',
      body: {
        ...data,
        __zip_type: ZIP_TYPE,
      },
    })
    if (res.status === ApiStatus.Success) {
      const zipData = this.transformZipData(res.data as Record<string, ApiResponse.ZipData>)
      const result: FetchResponse<R> = {
        ...res,
        data: zipData as R,
      }
      return result
    }
    return res
  }

  public async get<R extends Record<string, any>, J extends boolean = false>(
    url: string,
    data?: any,
    options: CustomRequestConfig = {
      isShowError: true,
    },
  ): Promise<J extends true ? R : FetchResponse<R>> {
    let dataStr = ''
    if (data && typeof data === 'object') {
      for (const key in data) {
        dataStr += `${key}=${data[key]}&`
      }
    }
    const newUrl = url.includes('?') ? `${url}&${dataStr}` : `${url}?${dataStr}`
    return this._fetch<R, J>(newUrl, {
      ...options,
      method: 'GET',
    })
  }

  /** 设置接口域名 */
  protected setBaseUrl() {
    return this.baseURL
  }

  /** 更新接口前面的域名 */
  private updateBaseURL() {
    const baseURL = this.setBaseUrl()
    this.baseURL = baseURL || ''
  }

  /** 请求拦截器 */
  public requestInterceptor(requestInterceptorFn: RequestInterceptor) {
    this._requestInterceptor = requestInterceptorFn
  }

  /** 响应拦截器 */
  public responseInterceptor(responseInterceptorFn: ResponseInterceptor) {
    this._responseInterceptor = responseInterceptorFn
  }

  protected transformZipData(data: Record<string, ApiResponse.ZipData>) {
    const keys = Object.keys(data)
    const result: Record<string, any> = {}
    for (const key of keys) {
      const { _fields1: field1, _fields2, _list } = data[key]
      result[key] = Array.isArray(_list) ? [] : {}
      if (field1 && field1.length > 0) {
        const _fields1 = field1.map(toCamelCase)
        const itemIdList = Object.keys(_list)
        itemIdList.forEach((itemId) => {
          if (!result[key][itemId]) {
            result[key][itemId] = {}
          }
          const itemValue = _list[itemId]
          itemValue.forEach((item, index) => {
            const field = _fields1[index]

            if (Array.isArray(item)) {
              if (_fields2) {
                const field2Keys = Object.keys(_fields2).map(toCamelCase)
                field2Keys.forEach((field2Key) => {
                  const field2Values = _fields2[field2Key].map(toCamelCase)
                  field2Values.forEach((field2Value, field2Index) => {
                    if (!result[key][itemId][field2Key]) {
                      result[key][itemId][field2Key] = {}
                    }
                    result[key][itemId][field2Key][field2Value] = item[field2Index]
                  })
                })
              } else {
                result[key][itemId][field] = item
              }
            } else {
              result[key][itemId][field] = item
            }
          })
        })
      }
    }
    return result
  }
}

export const request = new RequestService()
